import React, { useState, useEffect } from "react";
import { Constants } from "@eagerdog/constants";
import { IEvent, IShow, IShowElement } from "@eagerdog/interfaces";
import { apiService } from "src/services";

import Input from "src/components/Input/Input";
import Checkbox, { ICheck } from "src/components/Checkbox/Checkbox";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./WaitlistTab.module.scss";

interface IProps {
  event: IEvent,
  show: IShow,
  showType: string,
  showElements: ICheck[],
}

interface IPropsWaitlistElement {
  waitlist: IWaitlistItem,
  index: number,
  onChange(wl: IWaitlistItem, index: number): void
}

export interface IWaitlistItem {
  waitlist_option: string,
  show_element: string,
  max_entries: string,
  waitlist_size: string,
  waitlist_enabled: boolean
}

const WaitlistElement: React.FC<IPropsWaitlistElement> = (props) => {
  const [waitlist, setWaitlist] = useState<IWaitlistItem>(props.waitlist);

  const handleWaitlistToggle = (e: any) => {
    let _waitlist:IWaitlistItem = { ...waitlist, waitlist_enabled: !waitlist.waitlist_enabled };
    setWaitlist(_waitlist);
    props.onChange(_waitlist, props.index);
  }

  const handleMaxEntriesChange = (e: any) => {
    let _waitlist:IWaitlistItem = { ...waitlist, max_entries: e.target.value };
    setWaitlist(_waitlist);
    props.onChange(_waitlist, props.index);
  }

  const handleWaitlistSizeChange = (e: any) => {
    let _waitlist:IWaitlistItem = { ...waitlist, waitlist_size: e.target.value };
    setWaitlist(_waitlist);
    props.onChange(_waitlist, props.index);
  }

  return(
    <div className={waitlist.waitlist_enabled ? styles.WaitlistElement : `${styles.WaitlistElement} ${styles.disabled}`}>
      <div className={styles.title}>
        <div className={styles.titleInner}>{waitlist.show_element}</div>
        <div className={styles.toggle}>
          <Checkbox id={waitlist.show_element + "check"} onChange={handleWaitlistToggle} value={waitlist.waitlist_enabled} label={"Enable Waitlist"} />
        </div>
      </div>
      <div className={styles.data}>
        <div className={styles.inputWrap}>
          <Input
            className={styles.input}
            onChange={handleMaxEntriesChange}
            type="text"
            label="Entry Limit"
            placeholder="What's the maximum entries?"
            defaultValue={waitlist.max_entries}
          />
        </div>
        <div className={styles.inputWrap}>
          <Input className={styles.input} required={waitlist.waitlist_enabled ? true : false} disabled={waitlist.waitlist_enabled ? false : true} onChange={handleWaitlistSizeChange} type="text" label="Waitlist Size" placeholder="Whats the waitlist size?" defaultValue={waitlist.waitlist_size} />
        </div>
      </div>
    </div>
  );
}

const WaitlistTab: React.FC<IProps> = (props) => {
  const getWaitListSizingLabel = (sizing: string) => {
    let label:string = "Show Entry Limit";

    switch (sizing) {
      case Constants.waitlist_options.per_level_limit:
        label = "Per Level Limit";
      break;
      case Constants.waitlist_options.daily_judge_time_limit:
        label = "Daily Judge Time Limit";
      break;
    }

    return label;
  }

  const getWaitlistSizingValue = () => {
    if (props.show.waitlist_option && props.show.waitlist_option !== Constants.waitlist_options.none) {
      return { id: props.show.waitlist_option, value: getWaitListSizingLabel(props.show.waitlist_option) };
    }

    return { id: Constants.waitlist_options.show_entry_limit, value: getWaitListSizingLabel(Constants.waitlist_options.show_entry_limit) };
  }

  const [waitlists, setWaitlists] = useState<IWaitlistItem[]>([]);
  const [updatingShow, setUpdatingShow] = useState<boolean>(false);
  const [show, setShow] = useState<IShow>(props.show);
  const [waitlistSizing, setWaitlistSizing] = useState<IOption>(getWaitlistSizingValue());
  const [waitlistLimit, setWaitlistLimit] = useState<string>();

  const waitlistSizingOptions:IOption[] = [
    { id: Constants.waitlist_options.daily_judge_time_limit, value: "Daily Judge Limit" },
    { id: Constants.waitlist_options.show_entry_limit, value: "Show Entry Limit" },
    { id: Constants.waitlist_options.per_level_limit, value: "Per Level Limit" }
  ];

  useEffect(() => {
    let _wls:IWaitlistItem[] = [];
    let _showElements:ICheck[] = props.showElements.filter((_se: any) => _se.checked);

    if (props.show) {
      if (waitlists.length === 0) {
        for (let s in props.show.show_elements) {
          let _se:any = props.show.show_elements[s];

          _wls.push({
            waitlist_option: _se.waitlist_option || "None",
            show_element: _se.level ? _se.level : _se.show_element,
            max_entries: _se.max_entries?.toString() || "500",
            waitlist_size: _se.waitlist_size || "25",
            waitlist_enabled: _se.waitlist_enabled ? _se.waitlist_enabled : false
          });
        }
      } else {
        if (props.showType === props.show.show_type) {
          for (let s in _showElements) {
            let _se:any = _showElements[s];
            let hasValue = props.show.show_elements.filter((se: any) => { return se.show_element === _se.label || se.level === _se.label; });

            _wls.push({
              waitlist_option: _se.waitlist_option || "None",
              show_element: hasValue.length > 0 ? hasValue[0].level ? hasValue[0].level : hasValue[0].show_element : _se.label,
              max_entries: hasValue.length > 0 ? hasValue[0].max_entries?.toString() || "500" : "500",
              waitlist_size: hasValue.length > 0 ? hasValue[0].waitlist_size?.toString() || "25" : "25",
              waitlist_enabled: hasValue.length > 0 ? hasValue[0].waitlist_enabled || false : false
            });
          }
        } else {
          for (let s in _showElements) {
            let _se:any = _showElements[s];
            _wls.push({
              waitlist_option: _se.waitlist_option || "None",
              show_element:  _se.label,
              max_entries: "500",
              waitlist_size: "25",
              waitlist_enabled: false
            });
          }
        }
      }
    } else {
      for (let s in _showElements) {
        let _se:any = _showElements[s];

        _wls.push({
          waitlist_option: _se.waitlist_option || "None",
          show_element: _se.label,
          max_entries: "500",
          waitlist_size: "25",
          waitlist_enabled: false
        });
      }
    }

    if (_wls.length > 0) {
      setWaitlists(_wls);
    }
  }, [props.showElements, props.show, waitlists.length, props.showType]);

  const updateWaitlist = (_wl: IWaitlistItem, _index: number) => {
    let _waitlists:IWaitlistItem[] = [...waitlists];
    _waitlists[_index] = _wl;

    setWaitlists(_waitlists);
  }

  const updateShowWaitlist = (_waitlist: IWaitlistItem[]) => {
    if (show && !updatingShow) {
      setUpdatingShow(true);

      let payload:any = {
        ...show,
        waitlist_option: waitlistSizing.id,
        show_elements: show.show_elements.map((s: IShowElement, index: number) => {
          if (waitlistSizing.id === Constants.waitlist_options.per_level_limit) {
            let hasWaitlist = _waitlist.filter((w: IWaitlistItem) => { return s.level ? w.show_element === s.level :  w.show_element === s.show_element; });

            if (hasWaitlist.length > 0) {
              let se: any = { ...s, max_entries: Number(hasWaitlist[0].max_entries), waitlist_option: hasWaitlist[0].waitlist_option };


              if (hasWaitlist[0].waitlist_enabled) {
                se.waitlist_enabled = true;
                se.waitlist_size = Number(hasWaitlist[0].waitlist_size);
              }

              return se;
            } else {
              return s;
            }
          } else {
            let se: any = { ...s };

            delete se.waitlist_enabled;
            delete se.waitlist_size;
            delete se.max_entries;

            return se;
          }
        })
      };

      if (waitlistSizing.id === Constants.waitlist_options.daily_judge_time_limit ||
        waitlistSizing.id === Constants.waitlist_options.show_entry_limit) {
        payload.waitlist_limit = Number(waitlistLimit);
      } else {
        payload.waitlist_limit = undefined;
      }

      delete payload.__v;

      apiService.updateShow(props.event._id, show._id, payload).then((response) => {
        setShow(response);
        setUpdatingShow(false);
      }).finally(() => {
        toast.show({
          title: "Update Waitlist",
          content: "Waitlist has been successfully updated",
          duration: 10000,
          type: "success"
        });
      });
    }
  }

  const getWaitlistSizingDetails = () => {
    let details: any = <p className={styles.details}>Per Level Limits lets you set a maximum entry limit, enable a waitlist and set a waitlist size for each individual level at your show.</p>;

    switch (waitlistSizing.id) {
      case Constants.waitlist_options.daily_judge_time_limit:
        details = <p className={styles.details}>Daily Judge Time Limit lets you decide the amount of entries based on the amount of time available to the judge on that day. The value is determined in minutes (ex. 480 minutes is 8 hours). </p>;
      break;
      case Constants.waitlist_options.show_entry_limit:
        details = <p className={styles.details}>Show Entry Limit lets you set a maximum entry limit for the entire show, regardless of levels or judge hours.</p>;
      break;
      case Constants.waitlist_options.per_level_limit:
        details = <p className={styles.details}>Per Level Limits lets you set a maximum entry limit, enable a waitlist and set a waitlist size for each individual level at your show.</p>;
      break;
    }

    return details;
  }

  useEffect(() => {
    if (props.show.waitlist_limit !== undefined && props.show.waitlist_limit.toString().length > 0) {
      setWaitlistLimit(props.show.waitlist_limit.toString());
    } else if (waitlistSizing.id === Constants.waitlist_options.daily_judge_time_limit) {
      setWaitlistLimit((8 * 60).toString());
    } else if (waitlistSizing.id === Constants.waitlist_options.show_entry_limit) {
      setWaitlistLimit((500).toString());
    }
  }, [waitlistSizing, props.show.waitlist_limit]);

  return (
    <form onSubmit={(e: any) => { e.preventDefault(); updateShowWaitlist(waitlists); }}className={styles.WaitlistTab}>
      <div className={styles.inputWrap}>
        <Dropdown value={waitlistSizing} onChange={(e: any, value: IOption) => { setWaitlistSizing(value); }} label="Waitlist Sizing" options={waitlistSizingOptions} placeholder="" />
      </div>
      {getWaitlistSizingDetails()}
      {waitlistSizing.id === Constants.waitlist_options.per_level_limit && waitlists.map((wl: IWaitlistItem, index: number) => {
        return(
          <WaitlistElement
            key={index}
            waitlist={wl}
            index={index}
            onChange={(_wl: IWaitlistItem, _index: number) => {
              updateWaitlist(_wl, _index);
            }}
          />);
      })}
      {waitlistSizing.id !== Constants.waitlist_options.per_level_limit && <div className={styles.limitWrap}>
        <Input type="text" required onChange={(e:any) => { setWaitlistLimit(e.target.value); }} label={waitlistSizing.id === Constants.waitlist_options.daily_judge_time_limit ? "Total Judge Time (Minutes)" : "Entry Limit"} placeholder="" defaultValue={waitlistLimit} />
      </div>}
      <div className={styles.actions}>
        <Button type="submit">Save</Button>
      </div>
    </form>
  );
};

export default WaitlistTab;